import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartCard } from './charts/ChartPrimitives';
import { CHART, formatUnits } from '../lib/reporting-format';
import {
  formatUsageDay,
  niceStep,
  partLabel,
  type ChartSeries,
  type UsageBucket,
  type UsageChart,
} from '../lib/three-pl-usage-view';
import { Swatch } from './ThreePlCells';
import { ThreePlUsageTip } from './ThreePlUsageTip';

interface ShippedPerDayChartProps {
  chart: UsageChart;
  /** The server period, already formatted ("1 Oct to 8 Oct"). */
  rangeLabel: string;
}

/** dataKey of the 2px grey stub that marks an empty bucket (never in the key or tooltip). */
const STUB_KEY = '__empty';

type ChartDatum = Record<string, number | string> & { start: string };

interface UsageTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ChartDatum }>;
  buckets: UsageBucket[];
  series: ChartSeries[];
  bucketLabel: (bucket: UsageBucket) => string;
  seriesLabel: (series: ChartSeries) => string;
}

function UsageTooltip({ active, payload, buckets, series, bucketLabel, seriesLabel }: UsageTooltipProps) {
  const { t } = useTranslation('inventory');
  const start = payload?.[0]?.payload?.start;
  const bucket = active && start ? buckets.find((b) => b.start === start) : undefined;
  if (!bucket) return null;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="mb-1 font-semibold">{bucketLabel(bucket)}</p>
      {bucket.total === 0 ? (
        <p className="text-muted-foreground">{t('reporting.threePlUsage.chart.nothing')}</p>
      ) : (
        <>
          {series.map((s) => (
            <p key={s.dataKey} className="flex items-center gap-1.5 tabular-nums">
              <Swatch colour={s.colour} small />
              {`${seriesLabel(s)}: ${formatUnits(bucket.values[s.dataKey] ?? 0)}`}
            </p>
          ))}
          {series.length > 1 && (
            <p className="mt-1 font-medium tabular-nums">
              {t('reporting.threePlUsage.chart.total', { count: bucket.total })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Pieces shipped per day (or per Monday-start week past 62 days): stacked bars,
 * one colour per client on All clients or per kind for one client. No motion:
 * every Bar has `isAnimationActive={false}`. Ticks, tooltip and the screen
 * reader list share ONE day formatter, and recharts thins colliding ticks
 * itself. A day with nothing shipped is a flat grey mark, which the (i) names.
 */
function ShippedPerDayChartImpl({ chart, rangeLabel }: ShippedPerDayChartProps) {
  const { t, i18n } = useTranslation('inventory');
  const language = i18n.language;
  const { mode, buckets, series } = chart;

  const formatDay = React.useCallback((iso: string) => formatUsageDay(iso, language), [language]);
  const formatRange = React.useCallback(
    (from: string, to: string) =>
      t('reporting.threePlUsage.range', { from: formatDay(from), to: formatDay(to) }),
    [t, formatDay],
  );
  const bucketLabel = React.useCallback(
    (bucket: UsageBucket) =>
      bucket.start === bucket.end ? formatDay(bucket.start) : formatRange(bucket.start, bucket.end),
    [formatDay, formatRange],
  );
  const seriesLabel = React.useCallback((s: ChartSeries) => partLabel(s, t), [t]);

  const data = React.useMemo<ChartDatum[]>(
    () => buckets.map((bucket) => ({ ...bucket.values, start: bucket.start, [STUB_KEY]: 0 })),
    [buckets],
  );
  const ticks = React.useMemo(
    () => niceStep(buckets.reduce((max, bucket) => Math.max(max, bucket.total), 0)),
    [buckets],
  );
  const stubSize = React.useCallback(
    (_value: number | undefined | null, index: number) => (buckets[index]?.total === 0 ? 2 : 0),
    [buckets],
  );

  const lastSeries = series.length - 1;

  return (
    <ChartCard
      title={
        mode === 'week'
          ? t('reporting.threePlUsage.chart.titleWeek')
          : t('reporting.threePlUsage.chart.titleDay')
      }
      // -my-0.5 sits the 24px (i) on the title's 20px line.
      action={<ThreePlUsageTip name="chart" align="end" className="-my-0.5" />}
      description={
        // A long client name wraps inside its own key, beside its swatch.
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          {series.map((s) => (
            <span key={s.dataKey} className="inline-flex min-w-0 max-w-full items-start gap-1.5">
              <span className="flex h-4 shrink-0 items-center">
                <Swatch colour={s.colour} />
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{seriesLabel(s)}</span>
            </span>
          ))}
        </span>
      }
    >
      <div
        className="h-40"
        role="group"
        aria-label={t('reporting.threePlUsage.chart.ariaLabel', { range: rangeLabel })}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            accessibilityLayer
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART.grid} />
            <XAxis
              dataKey="start"
              tickFormatter={formatDay}
              interval="preserveStartEnd"
              minTickGap={12}
              tick={{ fontSize: 10, fill: CHART.axis }}
              tickLine={false}
              axisLine={{ stroke: CHART.axis }}
            />
            <YAxis
              ticks={ticks}
              domain={[0, ticks[ticks.length - 1] ?? 0]}
              allowDecimals={false}
              tick={{ fontSize: 10, fill: CHART.axis }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatUnits(value)}
              width={40}
            />
            <Tooltip
              cursor={{ fill: CHART.grid, opacity: 0.5 }}
              content={
                <UsageTooltip
                  buckets={buckets}
                  series={series}
                  bucketLabel={bucketLabel}
                  seriesLabel={seriesLabel}
                />
              }
            />
            <Bar
              dataKey={STUB_KEY}
              stackId="usage"
              fill={CHART.grid}
              minPointSize={stubSize}
              isAnimationActive={false}
              legendType="none"
              tooltipType="none"
              maxBarSize={28}
            />
            {series.map((s, index) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={seriesLabel(s)}
                stackId="usage"
                fill={s.colour}
                isAnimationActive={false}
                maxBarSize={28}
                radius={index === lastSeries ? [3, 3, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* No information only on hover (DESIGN.md §10): each bucket's total. */}
      <ul className="sr-only">
        {buckets.map((bucket) => (
          <li key={bucket.start}>{`${bucketLabel(bucket)}: ${formatUnits(bucket.total)}`}</li>
        ))}
      </ul>
    </ChartCard>
  );
}

export const ShippedPerDayChart = React.memo(ShippedPerDayChartImpl);
